import React, { useState } from 'react';
import styled from 'styled-components';

const ContractGenerator = () => {
  const [contractData, setContractData] = useState({
    contractNumber: '',
    clientName: '',
    clientAddress: '',
    clientEmail: '',
    clientPhone: '',
    projectDescription: '',
    totalCost: '',
    timeline: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    paymentTerms: '50% upfront, 50% on completion',
    additionalTerms: '',
    clientSignature: '',
    clientSignatureDate: '',
    companySignature: '',
    companySignatureDate: ''
  });

  const updateField = (field, value) => {
    setContractData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateContractNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `CON-${year}${month}-${random}`;
  };

  const handlePrint = () => {
    window.print();
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <ContractContainer>
      <Header>
        <Title>Contract Generator</Title>
        <ActionButtons>
          <Button onClick={() => updateField('contractNumber', generateContractNumber())}>
            Generate Contract #
          </Button>
          <PrintButton onClick={handlePrint}>Print Contract</PrintButton>
        </ActionButtons>
      </Header>

      <ContractForm>
        {/* Contract Header */}
        <ContractHeader>
          <CompanyInfo>
            <CompanyName>B&B Global Solutions</CompanyName>
            <CompanyDetails>
              Professional Software Development Services<br/>
              Email: contact@bbglobalsolutions.com<br/>
              Phone: (555) 123-4567
            </CompanyDetails>
          </CompanyInfo>
          <ContractInfo>
            <Field>
              <Label>Contract Number:</Label>
              <Input
                type="text"
                value={contractData.contractNumber}
                onChange={(e) => updateField('contractNumber', e.target.value)}
                placeholder="CON-2024-001"
              />
            </Field>
            <Field>
              <Label>Date:</Label>
              <DateDisplay>{getCurrentDate()}</DateDisplay>
            </Field>
          </ContractInfo>
        </ContractHeader>

        <ContractTitle>SOFTWARE DEVELOPMENT SERVICES AGREEMENT</ContractTitle>

        {/* Client Information */}
        <Section>
          <SectionTitle>CLIENT INFORMATION</SectionTitle>
          <FormGrid>
            <Field>
              <Label>Client Name:</Label>
              <Input
                type="text"
                value={contractData.clientName}
                onChange={(e) => updateField('clientName', e.target.value)}
                placeholder="Client Full Name"
              />
            </Field>
            <Field>
              <Label>Email:</Label>
              <Input
                type="email"
                value={contractData.clientEmail}
                onChange={(e) => updateField('clientEmail', e.target.value)}
                placeholder="client@email.com"
              />
            </Field>
            <Field>
              <Label>Phone:</Label>
              <Input
                type="tel"
                value={contractData.clientPhone}
                onChange={(e) => updateField('clientPhone', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </Field>
          </FormGrid>
          <Field>
            <Label>Address:</Label>
            <TextArea
              rows="3"
              value={contractData.clientAddress}
              onChange={(e) => updateField('clientAddress', e.target.value)}
              placeholder="Client Address"
            />
          </Field>
        </Section>

        {/* Project Details */}
        <Section>
          <SectionTitle>PROJECT DETAILS</SectionTitle>
          <Field>
            <Label>Project Description:</Label>
            <TextArea
              rows="4"
              value={contractData.projectDescription}
              onChange={(e) => updateField('projectDescription', e.target.value)}
              placeholder="Detailed description of the software development project, features, and deliverables..."
            />
          </Field>
          
          <FormGrid>
            <Field>
              <Label>Total Cost:</Label>
              <Input
                type="text"
                value={contractData.totalCost}
                onChange={(e) => updateField('totalCost', e.target.value)}
                placeholder="$10,000"
              />
            </Field>
            <Field>
              <Label>Timeline:</Label>
              <Input
                type="text"
                value={contractData.timeline}
                onChange={(e) => updateField('timeline', e.target.value)}
                placeholder="8-12 weeks"
              />
            </Field>
            <Field>
              <Label>Start Date:</Label>
              <Input
                type="date"
                value={contractData.startDate}
                onChange={(e) => updateField('startDate', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Expected Completion:</Label>
              <Input
                type="date"
                value={contractData.endDate}
                onChange={(e) => updateField('endDate', e.target.value)}
              />
            </Field>
          </FormGrid>

          <Field>
            <Label>Payment Terms:</Label>
            <Select
              value={contractData.paymentTerms}
              onChange={(e) => updateField('paymentTerms', e.target.value)}
            >
              <option value="50% upfront, 50% on completion">50% upfront, 50% on completion</option>
              <option value="100% upfront">100% upfront</option>
              <option value="33% upfront, 33% at milestone, 34% on completion">33% upfront, 33% at milestone, 34% on completion</option>
              <option value="25% upfront, 75% on completion">25% upfront, 75% on completion</option>
            </Select>
          </Field>
        </Section>

        {/* Contract Terms */}
        <Section>
          <SectionTitle>TERMS AND CONDITIONS</SectionTitle>
          <TermsContent>
            <TermsParagraph>
              <strong>1. SCOPE OF WORK:</strong> B&B Global Solutions ("Company") agrees to provide software development services 
              as described in the project description above. All work will be performed with professional standards and industry best practices.
            </TermsParagraph>

            <TermsParagraph>
              <strong>2. PAYMENT:</strong> Payment shall be made according to the payment terms specified above. 
              All payments are due within 30 days of invoice date unless otherwise specified.
            </TermsParagraph>

            <TermsParagraph>
              <strong>3. MONEY BACK GUARANTEE:</strong> B&B Global Solutions stands behind our work with a 100% satisfaction guarantee. 
              If the delivered software does not meet the agreed specifications and requirements outlined in this contract, 
              and we are unable to remedy the issues within 30 days of notification, the client is entitled to a full refund 
              of all payments made. This guarantee is valid for 90 days after project completion.
            </TermsParagraph>

            <TermsParagraph>
              <strong>4. TIMELINE:</strong> The Company will use reasonable efforts to complete the project within the specified timeline. 
              Delays caused by client feedback, change requests, or external dependencies may extend the delivery date accordingly.
            </TermsParagraph>

            <TermsParagraph>
              <strong>5. INTELLECTUAL PROPERTY:</strong> Upon full payment, all rights to the custom software developed specifically 
              for this project will transfer to the client. The Company retains rights to any pre-existing code, frameworks, 
              or general methodologies used in the development process.
            </TermsParagraph>

            <TermsParagraph>
              <strong>6. SUPPORT:</strong> The Company will provide 30 days of free bug fixes and technical support after project completion. 
              Additional support and maintenance can be arranged under a separate agreement.
            </TermsParagraph>

            <TermsParagraph>
              <strong>7. CONFIDENTIALITY:</strong> Both parties agree to maintain confidentiality of all proprietary information 
              shared during the course of this project.
            </TermsParagraph>

            <TermsParagraph>
              <strong>8. TERMINATION:</strong> Either party may terminate this agreement with 14 days written notice. 
              In case of termination, the client will pay for all work completed up to the termination date.
            </TermsParagraph>

            <Field>
              <Label>Additional Terms:</Label>
              <TextArea
                rows="3"
                value={contractData.additionalTerms}
                onChange={(e) => updateField('additionalTerms', e.target.value)}
                placeholder="Any additional terms or special conditions..."
              />
            </Field>
          </TermsContent>
        </Section>

        {/* Signatures */}
        <SignatureSection>
          <SectionTitle>SIGNATURES</SectionTitle>
          <SignatureContainer>
            <SignatureBlock>
              <SignatureLabel>CLIENT SIGNATURE:</SignatureLabel>
              <SignatureLine>
                <SignatureInput
                  type="text"
                  value={contractData.clientSignature}
                  onChange={(e) => updateField('clientSignature', e.target.value)}
                  placeholder="Digital signature or type name"
                />
              </SignatureLine>
              <SignatureInfo>
                <div>
                  <strong>Name:</strong> {contractData.clientName || '____________________'}
                </div>
                <div>
                  <strong>Date:</strong>
                  <DateInput
                    type="date"
                    value={contractData.clientSignatureDate}
                    onChange={(e) => updateField('clientSignatureDate', e.target.value)}
                  />
                </div>
              </SignatureInfo>
            </SignatureBlock>

            <SignatureBlock>
              <SignatureLabel>B&B GLOBAL SOLUTIONS:</SignatureLabel>
              <SignatureLine>
                <SignatureInput
                  type="text"
                  value={contractData.companySignature}
                  onChange={(e) => updateField('companySignature', e.target.value)}
                  placeholder="Authorized representative signature"
                />
              </SignatureLine>
              <SignatureInfo>
                <div>
                  <strong>Name:</strong> Authorized Representative
                </div>
                <div>
                  <strong>Title:</strong> Project Manager
                </div>
                <div>
                  <strong>Date:</strong>
                  <DateInput
                    type="date"
                    value={contractData.companySignatureDate}
                    onChange={(e) => updateField('companySignatureDate', e.target.value)}
                  />
                </div>
              </SignatureInfo>
            </SignatureBlock>
          </SignatureContainer>

          <AgreementNote>
            By signing this agreement, both parties acknowledge they have read, understood, and agree to be bound by all terms and conditions outlined above.
          </AgreementNote>
        </SignatureSection>
      </ContractForm>
    </ContractContainer>
  );
};

// Styled Components
const ContractContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
  background: white;
  min-height: 100vh;
  color: #333;
  
  @media print {
    padding: 0;
    box-shadow: none;
    max-width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media print {
    display: none;
  }
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  background: #3498db;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #2980b9;
  }
`;

const PrintButton = styled(Button)`
  background: #27ae60;
  
  &:hover {
    background: #219a52;
  }
`;

const ContractForm = styled.div`
  border: 2px solid #2c3e50;
  padding: 3rem;
  background: white;
  line-height: 1.6;
`;

const ContractHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  align-items: flex-start;
  border-bottom: 2px solid #2c3e50;
  padding-bottom: 1rem;
`;

const CompanyInfo = styled.div``;

const CompanyName = styled.h2`
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  font-weight: bold;
`;

const CompanyDetails = styled.div`
  color: #555;
  font-size: 0.9rem;
`;

const ContractInfo = styled.div`
  text-align: right;
`;

const ContractTitle = styled.h1`
  text-align: center;
  color: #2c3e50;
  margin: 2rem 0;
  font-size: 1.5rem;
  font-weight: bold;
  letter-spacing: 1px;
  border-bottom: 1px solid #bdc3c7;
  padding-bottom: 1rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #bdc3c7;
  padding-bottom: 0.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #bdc3c7;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const TextArea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #bdc3c7;
  border-radius: 4px;
  font-size: 1rem;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #bdc3c7;
  border-radius: 4px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const DateDisplay = styled.div`
  padding: 0.5rem;
  background: #f8f9fa;
  border: 1px solid #bdc3c7;
  border-radius: 4px;
  color: #2c3e50;
  font-weight: 500;
`;

const TermsContent = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 4px;
  border: 1px solid #bdc3c7;
`;

const TermsParagraph = styled.p`
  margin-bottom: 1rem;
  text-align: justify;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SignatureSection = styled(Section)`
  margin-top: 3rem;
  border-top: 2px solid #2c3e50;
  padding-top: 2rem;
`;

const SignatureContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const SignatureBlock = styled.div`
  border: 1px solid #bdc3c7;
  padding: 1.5rem;
  border-radius: 4px;
  background: #f8f9fa;
`;

const SignatureLabel = styled.div`
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 1rem;
  text-transform: uppercase;
  font-size: 0.9rem;
`;

const SignatureLine = styled.div`
  border-bottom: 2px solid #2c3e50;
  margin-bottom: 1rem;
  min-height: 50px;
  display: flex;
  align-items: end;
`;

const SignatureInput = styled.input`
  border: none;
  background: transparent;
  width: 100%;
  font-size: 1.2rem;
  font-family: cursive;
  color: #2c3e50;
  padding: 0.5rem 0;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    font-family: inherit;
    font-style: italic;
    color: #7f8c8d;
  }
`;

const SignatureInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #2c3e50;
`;

const DateInput = styled.input`
  border: none;
  background: transparent;
  color: #2c3e50;
  margin-left: 0.5rem;
  
  &:focus {
    outline: none;
  }
`;

const AgreementNote = styled.div`
  background: #e8f4f8;
  padding: 1rem;
  border-radius: 4px;
  border-left: 4px solid #3498db;
  font-style: italic;
  color: #2c3e50;
  text-align: center;
`;

export default ContractGenerator; 